import { ServerFile } from "@shared/interfaces/ProjectsFiles";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";

export interface AssistantSelectedFiles {
  id: string;
  name: string;
}

interface ProjectChatContextType {
  projectId: string | undefined;
  projectName: string | undefined;
  chatLog: {
    message: string | ReactNode | ReactNode[];
    sender: "user" | "bot";
    citations?: any[];
    intermediate_steps?: string | null;
    generated_images_s3_keys?: any;
  }[];
  setChatLog: React.Dispatch<
    React.SetStateAction<
      {
        message: string | ReactNode | ReactNode[];
        sender: "user" | "bot";
        citations?: any[];
        images?: any;
        intermediate_steps?: string | null;
        generated_images_s3_keys?: any;
      }[]
    >
  >;
  setProject: (
    id: string,
    name: string,
    report?: string,
    reportName?: string
  ) => void;
  addMessageToChat: (
    message: string | ReactNode | ReactNode[],
    sender: "user" | "bot",
    isChunk?: boolean,
    citations?: any[],
    generated_images_s3_keys?: any
  ) => void;
  clearChat: () => void;
  reportId?: string;
  reportName?: string;
  setFiles: (files: ServerFile[]) => void;
  files: ServerFile[];
  setSelectedFiles: React.Dispatch<
    React.SetStateAction<AssistantSelectedFiles[]>
  >;
  selectedFiles: AssistantSelectedFiles[];
}

const ProjectChatContext = createContext<ProjectChatContextType | undefined>(
  undefined
);

export const ProjectChatProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const [projectName, setProjectName] = useState<string | undefined>(undefined);
  const [reportName, setReportName] = useState<string | undefined>(undefined);
  const [chatLog, setChatLog] = useState<
    {
      message: string | ReactNode | ReactNode[];
      sender: "user" | "bot";
      citations?: any[];
      generated_images_s3_keys?: any;
    }[]
  >([]);
  const location = useLocation();
  const [reportId, setReportId] = useState<string | undefined>(undefined);
  const [files, setFiles] = useState<ServerFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<AssistantSelectedFiles[]>(
    []
  );

  const setProject = (
    id: string,
    name: string,
    report?: string,
    reportNameInitial?: string
  ) => {
    setProjectId(id);
    setProjectName(name);
    setReportId(report);
    setChatLog([]);
    setReportName(reportNameInitial);
  };

  const addMessageToChat = (
    message: string | ReactNode | ReactNode[],
    sender: "user" | "bot",
    isChunk = false,
    citations?: any[],
    generated_images_s3_keys?: any
  ) => {
    setChatLog((prevLog) => {
      const updatedLog = [...prevLog];

      if (sender === "bot" && isChunk) {
        const lastMessage = updatedLog[updatedLog.length - 1];
        if (lastMessage && lastMessage.sender === "bot") {
          lastMessage.message = message;
          if (citations) {
            lastMessage.citations = citations;
          }
        } else {
          updatedLog.push({
            message,
            sender,
            citations,
            generated_images_s3_keys,
          });
        }
      } else {
        updatedLog.push({
          message,
          sender,
          citations,
          generated_images_s3_keys,
        });
      }

      return updatedLog;
    });
  };

  const clearChat = () => {
    setChatLog([]);
  };
  return (
    <ProjectChatContext.Provider
      value={{
        projectId,
        projectName,
        chatLog,
        setChatLog,
        setProject,
        addMessageToChat,
        clearChat,
        reportId,
        reportName,
        setFiles,
        files,
        setSelectedFiles,
        selectedFiles,
      }}
    >
      {children}
    </ProjectChatContext.Provider>
  );
};

export const useProjectChat = () => {
  const context = useContext(ProjectChatContext);
  if (!context) {
    throw new Error("useProjectChat must be used within a ProjectChatProvider");
  }
  return context;
};
